/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Home, Users, Compass, Sparkles } from "lucide-react";

const ICONS = {
  housing: Home,
  whanau: Users,
  reintegration: Compass,
  addiction: Sparkles,
};

export default function Surveys() {
  const { surveyId } = useParams();
  if (surveyId) return <SurveyDetail surveyId={surveyId} />;
  return <SurveyIndex />;
}

function SurveyIndex() {
  const [surveys, setSurveys] = useState([]);
  const [latest, setLatest] = useState({});

  useEffect(() => {
    api.get("/surveys").then((r) => setSurveys(r.data || []));
    api.get("/survey-responses/latest").then((r) => setLatest(r.data || {})).catch(() => {});
  }, []);

  return (
    <div className="space-y-8 animate-fadeup">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-2">Ngā rangahau · inventories</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-charcoal mb-2">Four domain inventories</h1>
        <p className="text-charcoal/70 max-w-2xl">
          Deeper than the wheel. Six short questions per domain — housing, whānau (parental alienation),
          reintegration & decompression, and addiction recovery. These aren't tests. They're mirrors.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        {surveys.map((s) => {
          const Icon = ICONS[s.id] || Compass;
          const last = latest[s.id];
          return (
            <Link
              key={s.id}
              to={`/surveys/${s.id}`}
              data-testid={`survey-card-${s.id}`}
              className="group bg-muka rounded-2xl p-6 sm:p-7 border border-border hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(42,42,42,0.08)] transition-all"
              style={{ borderLeftWidth: 4, borderLeftColor: s.color }}
            >
              <Icon size={22} strokeWidth={1.4} style={{ color: s.color }} className="mb-3" />
              <h3 className="font-serif text-2xl text-charcoal mb-1">{s.title}</h3>
              <p className="text-xs text-muted-foreground italic mb-3">{s.kupu}</p>
              <p className="text-sm text-charcoal/70 leading-relaxed line-clamp-3">{s.intro}</p>
              {last && (
                <div className="mt-4 text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  Last taken · avg {last.average}/10
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function SurveyDetail({ surveyId }) {
  const nav = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [reflection, setReflection] = useState("");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/surveys/${surveyId}`).then((r) => {
      setSurvey(r.data);
      const init = {};
      r.data.questions.forEach((q) => (init[q.id] = 5));
      setAnswers(init);
    }).catch(() => toast.error("Could not load"));
  }, [surveyId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/survey-responses", {
        survey_id: surveyId,
        answers,
        reflection: reflection || null,
      });
      setDone(data);
    } catch {
      toast.error("Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  if (!survey) return <div className="font-serif italic text-charcoal/60">whiria…</div>;

  if (done) {
    return (
      <div className="max-w-xl mx-auto animate-fadeup">
        <div className="bg-muka rounded-3xl p-8 border border-border" style={{ borderTopWidth: 4, borderTopColor: survey.color }}>
          <div className="text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: survey.color }}>
            {survey.title}
          </div>
          <h2 className="font-serif text-3xl text-charcoal mb-3">Ka pai.</h2>
          <p className="text-charcoal/80 leading-relaxed mb-6">
            You sat with six questions. Average: <span className="font-serif text-2xl" style={{ color: survey.color }}>{done.average}/10</span>.
            That number isn't the point — the noticing is. You can come back to this any time and watch it shift.
          </p>
          {done.reflection && (
            <div className="bg-bone rounded-2xl p-4 italic text-charcoal/80 mb-6 font-serif">
              "{done.reflection}"
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => nav("/surveys")}
              data-testid="survey-back-index"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border hover:bg-bone"
            >
              <ArrowLeft size={16} /> back to inventories
            </button>
            <button
              onClick={() => nav("/dashboard")}
              data-testid="survey-back-dashboard"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ngahere text-muka hover:bg-ngahere/90"
            >
              Kāinga · home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const total = survey.questions.length + 1; // +1 for reflection
  const isReflectionStep = step === survey.questions.length;
  const q = !isReflectionStep ? survey.questions[step] : null;

  return (
    <div className="max-w-xl mx-auto animate-fadeup">
      <Link to="/surveys" className="text-sm text-muted-foreground hover:text-charcoal flex items-center gap-1 mb-4" data-testid="survey-back-link">
        <ArrowLeft size={14} /> all inventories
      </Link>
      <div className="flex gap-1 mb-6">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "" : "bg-border"}`}
            style={i <= step ? { backgroundColor: survey.color } : {}}
          />
        ))}
      </div>

      <div className="bg-muka rounded-3xl p-7 sm:p-9 border border-border" style={{ borderTopWidth: 4, borderTopColor: survey.color }}>
        {step === 0 && (
          <div className="mb-6 -mt-2">
            <div className="text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: survey.color }}>
              {survey.title}
            </div>
            <p className="text-xs text-muted-foreground italic mb-4">{survey.kupu}</p>
            <p className="text-sm text-charcoal/70 leading-relaxed mb-6">{survey.intro}</p>
            <div className="kaupapa-divider mb-6" />
          </div>
        )}

        {q && (
          <div className="animate-fadeup">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
              {step + 1} / {survey.questions.length}
            </div>
            <p className="font-serif text-2xl sm:text-3xl text-charcoal leading-snug mb-8">
              {q.text}
            </p>
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-xs text-muted-foreground">{q.labels[0]}</span>
              <span className="font-serif text-4xl" style={{ color: survey.color }}>{answers[q.id]}</span>
              <span className="text-xs text-muted-foreground">{q.labels[1]}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={answers[q.id] ?? 5}
              onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: Number(e.target.value) }))}
              data-testid={`survey-slider-${q.id}`}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: survey.color }}
            />
          </div>
        )}

        {isReflectionStep && (
          <div className="animate-fadeup">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
              Whakaaro · reflection
            </div>
            <p className="font-serif text-2xl text-charcoal leading-snug mb-5">
              {survey.reflection_prompt}
            </p>
            <textarea
              rows={5}
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              data-testid="survey-reflection"
              placeholder="Optional. This stays with you."
              className="w-full px-4 py-3 rounded-xl border border-border bg-bone focus:outline-none focus:ring-2 focus:ring-ngahere"
            />
          </div>
        )}

        <div className="flex justify-between mt-8">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              data-testid="survey-back"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-charcoal/70 hover:bg-bone"
            >
              <ArrowLeft size={16} /> back
            </button>
          ) : (
            <span />
          )}
          {isReflectionStep ? (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="survey-submit"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-muka disabled:opacity-50"
              style={{ backgroundColor: survey.color }}
            >
              <Check size={18} /> {submitting ? "..." : "Tukua · save"}
            </button>
          ) : (
            <button
              onClick={() => setStep(step + 1)}
              data-testid="survey-next"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-muka"
              style={{ backgroundColor: survey.color }}
            >
              Haere tonu <ArrowRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}